import { $$TARGET, } from '@ballpark/realtime-crdt';
import { useOnHistoryReplay, } from '@ballpark/realtime-plugin-history';
import { cloneDeep } from 'lodash-es';
import { reaction } from 'mobx';
import { useCallback, useEffect, useState } from 'react';
import { Map as YMap } from 'yjs';
/**
 * Hook that allows using proxied rich text values in the TextEditor component. This is necessary
 * because the TextEditor component doesn't handle proxies.
 *
 * It also handles history events that target the value.
 *
 * @param parent The parent value that contains the rich text value, must be a proxied object for
 * realtime and history to work.
 * @param key the key of the rich text value in the parent object.
 * @returns A plain rich text value that can be passed to the TextEditor component.
 */
export function useProxiedTextEditorValue(parent, key) {
    // We need to convert the value to a plain object as Plate doesn't
    // handle proxies.
    const [safeValue, setSafeValue] = useState(() => cloneDeep((parent === null || parent === void 0 ? void 0 : parent[key]) || []));
    // Update the value we pass to TextEditor if the description was edited by another user
    useEffect(() => {
        if (!parent)
            return;
        return reaction(() => parent[key], (value) => {
            var _a, _b;
            if (!value)
                return;
            // TODO: add to @ballpark/realtime-crdt methods for detecting if a value was last changed by this
            // client
            const yValue = value[$$TARGET];
            const currentId = (_a = yValue === null || yValue === void 0 ? void 0 : yValue.doc) === null || _a === void 0 ? void 0 : _a.clientID;
            const lastId = (_b = yValue === null || yValue === void 0 ? void 0 : yValue._item) === null || _b === void 0 ? void 0 : _b.lastId.client;
            // if the value was last changed by a different client, update it, this will trigger a
            // re-render of the TextEditor and show their changes
            if (currentId !== lastId) {
                setSafeValue(cloneDeep(value));
            }
        });
    }, [parent, key]);
    // Check if the history event targeted the value, force an update if so. This is necessary
    // because normally changes in value don't trigger a re-render due to the way plate/slate works.
    const updateIfChanged = useCallback((event) => {
        var _a;
        if (!parent)
            return;
        // access yValue via the parent object as value itself may be undefined
        const yValue = (_a = parent[$$TARGET]) === null || _a === void 0 ? void 0 : _a.get(key);
        if (!yValue)
            return;
        const hasChanged = [...event.changedParentTypes.values()].some((value) => {
            return value.some((event) => {
                return [...event.transaction.changed.entries()].some(([target, keys]) => {
                    if (!(target instanceof YMap))
                        return false;
                    return [...keys].some((key) => {
                        if (!key)
                            return;
                        return target.get(key) === yValue;
                    });
                });
            });
        });
        if (!hasChanged)
            return;
        setSafeValue(cloneDeep(parent[key]));
    }, [parent, key]);
    useOnHistoryReplay(updateIfChanged);
    return safeValue;
}
